import { ref, reactive, computed } from 'vue'
import { defineStore } from 'pinia'
import patientapi from "@/api/patientapi"
import appuserapi from "@/api/appuserapi"
import gen from "@/functions/general.js"
import { createSuggestionsFromCclTextList } from '@/functions/cclTextSuggestions.js'
import { getPatientTasksForSticky } from '@/functions/office'
import { findById, getTextBasedOnLanguage } from '@/functions/expression'
import { useAppUserStore } from '@/stores/appUser';
import { getNewPatientInfo, getNewPharmacyInfo, findMedicationPreference } from '@/functions/preferenceUtils';
import { serializeDoses, toDoseDto } from '@/functions/dosageUtils';
import { Consultation, PatientCommonExpression, FolderPatientApi, PatientSummaryResponse, PatientTreatmentResponse, PatientSummaryResponseSummaryTypeEnum, AssessmentExpr, PatientDocument, UserAppointmentResponse, PatientLightResponse, ConceptGroupResponse, AppointmentRequest } from '@/apiGenerate/medserver'
import { medServerApiConfig } from '@/config/medserver-api.config'
import { ApiError } from '@/models/share.model'
import { PatientDocumentOption, PatientDocumentUiDto, PatientDocumentVersionOption } from '@/models/patientDocument.model'
import { getPatientDocumentVersion } from '@/utils/patientDocument-utils'
import { useI18n } from 'vue-i18n'
import { useComputeRelativeDate } from '@/compsables/useComputeRelativeDate'
import { EventInput } from '@fullcalendar/core'

export const SapConceptStatus = {
  Idle: 'idle',
  Loading: 'loading',
  Error: 'error',
} as const;

export type SapConceptStatus = typeof SapConceptStatus[keyof typeof SapConceptStatus];

export const useCurrentEntryStore = defineStore('currentEntry', () => {

  const { t } = useI18n();
  const folderPatientApi = new FolderPatientApi(medServerApiConfig);
  const appUserStore = useAppUserStore();
  const showAnnotations = ref(false);
  const computeRelativeDate = useComputeRelativeDate();
  const sapConceptStatus = ref<SapConceptStatus>(SapConceptStatus.Idle);
  let conceptRequest: AbortController | null = null;

  //PatientCalendar
  const currentPatientCalendar = ref(null);

  function setCurrentPatientCalendar(pwrPatient: any) {
    currentPatientCalendar.value = pwrPatient
  }

  const appointmentId = ref<string>(undefined);
  const currentEvent = ref<EventInput>(undefined);

  const setAppointmentId = (newAppointmentId: string) => {
    appointmentId.value = newAppointmentId;
  }

  const setCurrentEvent = (newCurrentEvent: EventInput) => {
    currentEvent.value = newCurrentEvent;
  }

  // Move appointment
  const moveAppointmentFromCalendar = ref<AppointmentRequest>(undefined);
  const moveAppointmentFromPatient = ref<UserAppointmentResponse>(undefined);
  const patientLightResponse = ref< PatientLightResponse>(undefined);
  const isMoveAppointmentAction = ref<boolean>(false);

  const setIsMoveAppointmentAction = (newIsMoveAppointmentAction: boolean) => {
    isMoveAppointmentAction.value = newIsMoveAppointmentAction;
  }

  const setMoveAppointmentFromCalendar = (appointmentRequest: AppointmentRequest) => {
    moveAppointmentFromCalendar.value = appointmentRequest;
  }

  const setMoveAppointmentFromPatient = (userAppointmentResponse: UserAppointmentResponse, pwrPatient: any) => {
    moveAppointmentFromPatient.value = userAppointmentResponse;
    currentPatientCalendar.value = pwrPatient;

    if (pwrPatient) {
      patientLightResponse.value = {
        patientId: pwrPatient.personWithRoleId,
        firstname: pwrPatient.firstName,
        lastname: pwrPatient.lastName,
        birthdate: pwrPatient.birthDate,
        serialNumber: pwrPatient.cnsHealthInsurance?.cnsAffiliationNumber
      }
    } else {
      patientLightResponse.value = undefined;
    }    
  }

  //Reload righBar data in clinical folder
  const patientHistoryResponse = ref<PatientSummaryResponse[]>([]);
  const patientEcosystemResponse = ref<PatientSummaryResponse[]>([]);
  const patientMonitoredResponse = ref<PatientSummaryResponse[]>([]);
  const sapConcept = ref<ConceptGroupResponse>(null);

  function setPatientHistoryResponse(newPatientHistoryResponse: PatientSummaryResponse[]) {
    patientHistoryResponse.value = newPatientHistoryResponse;
  }

  const setPatientEcosystemResponse = (newPatientEcosystemResponses: PatientSummaryResponse[]) => {
    patientEcosystemResponse.value = newPatientEcosystemResponses;
  }

  const setPatientMonitoredResponse = (newPatientMonitoredResponses: PatientSummaryResponse[]) => {
    patientMonitoredResponse.value = newPatientMonitoredResponses;

    patientMonitoredResponse.value.forEach((expr: PatientSummaryResponse) => {
        expr.text = getTextBasedOnLanguage(expr, appUserStore.currentAppUserData?.preferences?.sctLanguage);
        expr.hasExclamation = false;
      });

      patientMonitoredResponse.value.sort((a: PatientSummaryResponse, b: PatientSummaryResponse) => {return a.text > b.text ? 1 : -1});

      //UI Index (AFTER sort)
      let uiIndex = 0;
      patientMonitoredResponse.value.forEach(expr => {
        expr.id = gen.newId("rBarMonitored", ++uiIndex)
      });
  }

  const setPatientSummaryResponse =  (newPatientSummaryResponse:  PatientSummaryResponse[]) => {
      patientHistoryResponse.value = newPatientSummaryResponse.filter((patientSummary: PatientSummaryResponse) => patientSummary.summaryType === PatientSummaryResponseSummaryTypeEnum.History);
      patientEcosystemResponse.value = newPatientSummaryResponse.filter((patientSummary: PatientSummaryResponse) => patientSummary.summaryType === PatientSummaryResponseSummaryTypeEnum.Ecosystem);

      setPatientMonitoredResponse(newPatientSummaryResponse.filter((patientSummary: PatientSummaryResponse) => patientSummary.summaryType === PatientSummaryResponseSummaryTypeEnum.Monitored));


  }

  const reloadHistoryData = ref<boolean>(false);
  const reloadMonitoredData = ref<boolean>(false);
  const reloadEcosystemData = ref<boolean>(false);
  const reloadTreatmentData = ref<boolean>(false);
  const reloadSummaryData = ref<boolean>(false);

  const setReloadHistoryData = (newValue: boolean) => {
    reloadHistoryData.value = newValue;
  }

  const setReloadMonitoredData = (newValue: boolean) => {
    reloadMonitoredData.value = newValue;
  }

  const setReloadEcosystemData = (newValue: boolean) => {
    reloadEcosystemData.value = newValue;
  }

  const setReloadTreatmentData = (newValue: boolean) => {
    reloadTreatmentData.value = newValue;
  }

  const setReloadSummaryData = (newValue: boolean) => {
    reloadSummaryData.value = newValue;
  }

  // ===== LOADING STATE =====
  
  const loadingState = ref({
    currentEntry: false,
    patient: false,
    allEntries: false,
    concepts: false,
    commonConcepts: false,
  })

  const isLoading = computed(() => 
    Object.values(loadingState.value).some(v => v)
  )

  const isFullyLoaded = computed(() => 
    loadingState.value.currentEntry === false &&
    loadingState.value.allEntries === false &&
    loadingState.value.commonConcepts === false &&
    _currentEntry.value !== null
  )

  // ===== RAW DATA (internal) =====
  
  const _currentEntry = ref(null)       // From getLastEntry
  const _allEntriesData = ref(null)     // From getAllEntries
  const _conceptsData = ref(null)       // concepts (history) from getConcept
  const _commonConceptsData = ref(null) // commonConcepts (medication) from getCommonConcept

  // ===== SIMPLE REFS (unchanged from original) =====
  
  const currentPatient = ref(null)
  const csList = ref<Consultation[]>([]);
  const ecosytems = ref<PatientSummaryResponse[]>([]);
  const currentNullCclId = ref(null)
  const currentDocId = ref('');
  const currentDoc = ref<PatientDocumentUiDto>(null);
  const selectedDocOption = ref<PatientDocumentOption>();
  const selectedDocVersionOption = ref<PatientDocumentVersionOption>();

  // ===== HELPER FUNCTIONS =====

  function dedupeById<T>(arr: T[], idKey: string): T[] {
    const map = new Map(arr.map((item: any) => [item[idKey], item]))
    return Array.from(map.values())
  }

  function getCurrentNullCcl(entry: any) {
    const currentEntryId = entry?.relationshipId
    if (!currentEntryId) return null
    return entry.conclusionGroups
      .find((cclGroup: any) => 
        cclGroup.relationshipId === currentEntryId && cclGroup.groupOrder === 0
      )
  }

  function resolveExpressionRefs(items: any[], allExpr: any[]): any[] {
    if (!items || !allExpr) return []

    return items.map((item: any) => {
      const resolved = gen.deepCopy(item)

      // Resolve definingSapExpression
      if (item.definingSapExpressionId) {
        resolved.definingSapExpression = findById(allExpr, 'expressionId', item.definingSapExpressionId)
      }

      // Resolve concept_expressions
      if (item.concept_expressions) {
        resolved.concept_expressions = item.concept_expressions
          .map((ref: any) => findById(allExpr, 'expressionId', ref.expressionId))
          .filter(Boolean)
          .sort((a: any, b: any) => {
            return new Date(b.timelineStart).getTime() - new Date(a.timelineStart).getTime()
              || new Date(b.updated).getTime() - new Date(a.updated).getTime()
          })
      }

      // Resolve doseTimings
      if (item.doseTimings) {
        resolved.doseTimings = item.doseTimings
          .map((dt: any) => {
            const closestExpression = findById(allExpr, 'expressionId', dt.expressionId)
            return closestExpression
              ? { ...dt, closestExpression: gen.deepCopy(closestExpression) }
              : dt
          })
          .sort((a: any, b: any) => {
            return new Date(b.timelineStart).getTime() - new Date(a.timelineStart).getTime()
              || new Date(b.updated).getTime() - new Date(a.updated).getTime()
          })
      }

      // Resolve currentEntryStatus.closestExpression
      if (item.currentEntryStatus?.priorDoseTiming?.expressionId) {
        const closestExpression = findById(
          allExpr,
          'expressionId',
          item.currentEntryStatus.priorDoseTiming.expressionId
        )
        if (closestExpression) {
          resolved.currentEntryStatus = {
            ...item.currentEntryStatus,
            closestExpression: gen.deepCopy(closestExpression)
          }
        }
      }

      return resolved
    })
  }

  // ===== COMPUTED: MERGED COLLECTIONS =====

  const allExpressions = ref<Array<PatientCommonExpression>>([]);
  const patientDocument = ref<Array<PatientDocument>>([]);

  const allPatientDocuments = computed(() => {
    const current =  _currentEntry.value.conclusionGroups.flatMap((cg : any) => cg.templateDocArr.flatMap((template : any) => template.documentExpression))?? []
    return dedupeById([...current, ...patientDocument.value], 'patientDocumentId')  //Current must be last to correctly update
  })

  const allEntries = computed(() => {
    const current = _currentEntry.value?.clinical?.entries ?? []
    const historical = _allEntriesData.value?.clinical?.entries ?? []
    return [...historical, ...current]
  })

  // ===== COMPUTED: RESOLVED TRANSVERSAL =====

  const concepts = computed(() => {
    const raw = _conceptsData.value
    if (!raw) return []
    return resolveExpressionRefs(raw, allExpressions.value)
  })

  const commonConcepts = computed(() => {
    const raw = _commonConceptsData.value
    if (!raw) return []
    return resolveExpressionRefs(raw, allExpressions.value)
  })
  
  // ===== HELPER GETTERS (for common access patterns) =====
 const currentEntry = computed(() =>
    _currentEntry.value ?? ""
  )

  const currentEntryId = computed(() =>
    _currentEntry.value?.relationshipId ?? ""
  )

  const isEditable = computed(() => 
    _currentEntry.value?.isEditable ?? false
  )

  const currentEntryDate = computed<string>(() =>
    _currentEntry.value?.entryStart ?? ""
  )

  const hasCurrentEntry = computed(() =>
    _currentEntry.value !== null
  )

  const currentPatientId = computed(() => 
    currentPatient.value?.personWithRoleId ?? ""
  )

  const currentHealthPro = computed(() => 
    _currentEntry.value?.healthPro ?? null
  )

  const currentDelegateUuid = computed<string | null>(() => {
    const entry = _currentEntry.value
    if (!entry || entry.secondaryHealthPro == null) return null
    return entry.healthPro?.personWithRoleId ?? null
  })

  // ===== LOADING FUNCTIONS =====
  let loadPatientFullPromise: Promise<void> | null = null

  async function loadPatientFull(patientId: string, options?: { force?: boolean }) {
    const force = options?.force ?? false
    // If same patient and not forced, just reload current entry
    if (patientId === currentPatientId.value && !force) {
      return reloadCurrentEntry()
    }
    loadPatientFullPromise = doLoadPatientFull(patientId)
    try {
      await loadPatientFullPromise
    } finally {
      loadPatientFullPromise = null
    }
  }

  async function doLoadPatientFull(patientId: string) {
    // LoadingStat
    loadingState.value = {
      currentEntry: true,
      allEntries: true,
      concepts: true,
      commonConcepts: true,
      patient: true,
    }
    // Clear previous patient
    _currentEntry.value = null
    currentPatient.value = null
    currentNullCclId.value = null
    _allEntriesData.value = null
    _conceptsData.value = null
    _commonConceptsData.value = null
    try {
      // Step 1: Patient info + current entry in parallel
      const [patientRes, entryRes, notes] = await Promise.all([
        patientapi.getPatientInfo(patientId),
        getLastConsultation(patientId),
        getPatientTasksForSticky(patientId),
      ])
      check403(patientRes, entryRes)

      currentPatient.value = patientRes.patient
      modalPatient.value = patientRes.patient
      currentPatientNotes.value = notes
      modalPatientNotes.value = notes
      _currentEntry.value = entryRes
      currentNullCclId.value = entryRes ? getCurrentNullCcl(entryRes)?.conclusionGroupId ?? null : null
      loadingState.value.patient = false
      loadingState.value.currentEntry = false
      
      // Step 2: Historical + Concepts + CommonConcepts in parallel
      if (entryRes) {
        _allEntriesData.value = []
        _conceptsData.value = []
        _commonConceptsData.value = []
      } else {
        _allEntriesData.value = []
        _conceptsData.value = []
        _commonConceptsData.value = []
      }
      
      loadingState.value.allEntries = false
      loadingState.value.concepts = false
      loadingState.value.commonConcepts = false
    } catch (error) {
      loadingState.value = {
        currentEntry: false,
        allEntries: false,
        concepts: false,
        commonConcepts: false,
        patient: false,
      }
      throw error
    }
  }

  async function getLastConsultation(patientId: string): Promise<Consultation | null> {
    try {
      const res = await folderPatientApi.getLastConsultation({ patientId });
      return res;
    } catch (error: any) {
      if (error.status === "LAST_CONSULTATION_NOT_FOUND") {
        return null;
      }
      throw error;
    }
}

  async function reloadCurrentEntry() {
    const patientId = currentPatientId.value
    if (!patientId) return     
    loadingState.value.currentEntry = true;
    try {
      const res = await folderPatientApi.getLastConsultation({patientId : patientId});
      _currentEntry.value = res;
      currentNullCclId.value = getCurrentNullCcl(res)?.conclusionGroupId ?? null;
      csList.value = csList.value.map(item => item.relationshipId === res.relationshipId ? { ...item, ...res } : item );
      csList.value  = csList.value.sort((a,b) => new Date(b.entryStart).getTime() - new Date(a.entryStart).getTime());
      return res;
    } finally {
      loadingState.value.currentEntry = false
    }
  }

  function setCurrentEntryDate(newIso: string) {
    if (_currentEntry.value) {
      _currentEntry.value = { ..._currentEntry.value, entryStart: newIso };
    }
  }

  async function reloadAfterMedicationChange() {
    const patientId = currentPatientId.value
    if (!patientId) return    
    loadingState.value.currentEntry = true

    try {
      const [entryRes] = await Promise.all([
        folderPatientApi.getLastConsultation({patientId : patientId})
      ])

      _currentEntry.value = entryRes
      _commonConceptsData.value = []
      currentNullCclId.value = getCurrentNullCcl(entryRes)?.conclusionGroupId ?? null
      csList.value = csList.value.map(item => item.relationshipId === entryRes.relationshipId ? { ...item, ...entryRes } : item );
      reloadTreatmentData.value = true;
    } finally {
      loadingState.value.currentEntry = false
    }
  }

function findExpression(expr: PatientCommonExpression) {
  return csList.value
    .find(cs => cs.relationshipId === expr.entry.relationshipId)?.conclusionGroups
      .find(cg => cg.conclusionGroupId === expr.conclusionGroup.conclusionGroupId)?.resultsArr
        .find(ex => ex.expressionId === expr.expressionId);

}

function updateExpressionAfterCorrection(expr: PatientCommonExpression,newText: string): void {

  const expression = findExpression(expr);
  if (expression) {
    expression.expression = expr;
    expression.text = newText;
    expression.dbText = newText;
  }

  // Update related assessment expressions (e.g. group title)
  csList.value
    .flatMap(cs => cs.conclusionGroups)
    .map(cg => cg.assessmentExpr)
    .filter(
      (ae): ae is AssessmentExpr =>
        ae?.expressionId === expr.expressionId
    )
    .forEach(ae => {
      ae.expression = expr;
      ae.buttonText = newText;
    });

}

  async function reloadAfterHistoryChange() {
    const patientId = currentPatientId.value
    if (!patientId) return

    loadingState.value.currentEntry = true
    loadingState.value.concepts = true

    try {
      const [entryRes, conceptsRes] = await Promise.all([
        patientapi.getLastEntry(patientId),
        patientapi.getConcept(patientId)
      ])

      if (entryRes.error === 403 || conceptsRes.error === 403) {
        throw new Error('USER_FORBIDDEN')
      }

      _currentEntry.value = entryRes
      _conceptsData.value = conceptsRes?.clinical?.concepts ?? []
      currentNullCclId.value = getCurrentNullCcl(entryRes)?.conclusionGroupId ?? null
      csList.value = csList.value.map(item => item.relationshipId === entryRes.relationshipId ? { ...item, ...entryRes } : item );

    } finally {
      loadingState.value.currentEntry = false
      loadingState.value.concepts = false
    }
  }

  // ===== ACTIONS =====

  function clearCurrentEntry() {

    csList.value = [];
    _currentEntry.value = null
    _allEntriesData.value = null
    _conceptsData.value = null
    _commonConceptsData.value = null
    currentPatient.value = null
    currentNullCclId.value = null
    currentDoc.value = null
    currentDocId.value = ''
    loadingState.value = {
      currentEntry: false,
      patient: false,
      allEntries: false,
      concepts: false,
      commonConcepts: false,
    }
    currentPatientNotes.value = null
    setMiddleTab('resultSuggestions');    
  }

  async function reloadPatientOnly() {
    const modalPatientId = modalPatient.value.personWithRoleId;
    if (!modalPatientId) return;

    const isCurrentPatient = currentPatientId.value === modalPatientId;
    if (isCurrentPatient) loadingState.value.patient = true;

    try {
      const { patient } = await patientapi.getPatientInfo(modalPatientId);
      modalPatient.value = patient;
      if (isCurrentPatient) currentPatient.value = patient;
    } finally {
      loadingState.value.patient = false;
    }
  }

  const check403 = (...responses: any[]) => {
    if (responses.some(r => r?.error === 403)) {
      throw new Error('USER_FORBIDDEN')
    }
  }

  async function refreshPatientMeta(patientId: string) {
    loadingState.value.patient = true
    loadingState.value.concepts = true
    loadingState.value.commonConcepts = true
    try {
      const [patientRes, conceptsRes, commonConceptsRes] = await Promise.all([
        patientapi.getPatientInfo(patientId),
        patientapi.getConcept(patientId),
        patientapi.getCommonConcept(patientId),
      ])
      check403(patientRes, conceptsRes, commonConceptsRes)
      currentPatient.value = patientRes.patient
      _conceptsData.value = conceptsRes?.clinical?.concepts ?? []
      _commonConceptsData.value = commonConceptsRes?.clinical?.commonConcepts ?? []
    } finally {
      loadingState.value.patient = false
      loadingState.value.concepts = false
      loadingState.value.commonConcepts = false
    }
  }

  async function createNewEntry(patientId: string, instituteId: string, creatorId: string | null) {
    const resp = await  folderPatientApi.createNewConsultations({
      patientId: patientId,
      createNewConsultationsRequest : {
      instituteWithRoleId: instituteId,
      healthProId : creatorId
      }
    }).catch((e: ApiError) => {
      throw e;
    })
    // Wait for any in-progress loadPatientFull to complete first
    if (loadPatientFullPromise) {
      await loadPatientFullPromise
    }

    // Set new current entry
    _currentEntry.value = resp
    currentNullCclId.value = getCurrentNullCcl(resp)?.conclusionGroupId ?? null
    // Refresh server-dependent data
    //await refreshPatientMeta(patientId)
    return resp
  }

  function updateExpression(updatedExpr: any) {
    if (!_currentEntry.value?.clinical?.expressions) return
    const expressions = [..._currentEntry.value.clinical.expressions]
    const exprIndex = expressions.findIndex((expr: any) => expr.expressionId === updatedExpr.expressionId)
    if (exprIndex === -1) {
      expressions.push(updatedExpr)  // Add if not found
    } else {
      expressions[exprIndex] = updatedExpr  // Replace if found
    }
    _currentEntry.value = {
      ..._currentEntry.value,
      clinical: {
        ..._currentEntry.value.clinical,
        expressions
      }
    }
  }

  function isExprOfCurrEntry(expr: any) {
    if (!expr) return false;      //in case of allMedic dashboard
    const cclId = expr.conclusionGroup.conclusionGroupId  //Don't use : expr.expression_ccl.conclusionGroupId    (it could be a linked expr of a concept)
    const currCclIds = allEntries.value
      .filter((entry: any) => entry.relationshipId === currentEntryId.value)
      .flatMap((entry: any) => entry.conclusionGroups.map((ccl: any) => ccl.conclusionGroupId))
    return currCclIds.includes(cclId)
  }

  //poppedSapExpr
  const poppedSapExpr = ref<PatientTreatmentResponse>(null);

  const poppedSapNewName = ref("");
  const poppedSapNewRouteTerm = ref("");
  const poppedSapNewGalenic = ref("");

  function setPoppedSapExpr (sapExpr: PatientTreatmentResponse) {
    poppedSapExpr.value = sapExpr
    poppedSapNewName.value = sapExpr?.matchedText    
    poppedSapNewRouteTerm.value = sapExpr?.sctAdministrationRouteTerm
    poppedSapNewGalenic.value = sapExpr?.galenic
    loadSapConcept();
  }

  function patchPoppedSapExpr(sapExpr: any) {
    if (!poppedSapExpr.value) {
      setPoppedSapExpr(sapExpr);
      return;
    }
    const prevSapExpr = poppedSapExpr.value;
    const prevName = prevSapExpr.matchedText;
    const prevRoute = prevSapExpr.sctAdministrationRouteTerm;
    const prevGalenic = prevSapExpr.galenic;

    Object.assign(prevSapExpr, sapExpr);
    prevSapExpr.isOptimistic = false;

    if (poppedSapNewName.value === prevName) poppedSapNewName.value = prevSapExpr.matchedText;
    if (poppedSapNewRouteTerm.value === prevRoute) poppedSapNewRouteTerm.value = prevSapExpr.sctAdministrationRouteTerm;
    if (poppedSapNewGalenic.value === prevGalenic) poppedSapNewGalenic.value = prevSapExpr.galenic;
    loadSapConcept();
  }

  function patchSapConcept(fields: Partial<ConceptGroupResponse>) {
    if (sapConcept.value) Object.assign(sapConcept.value, fields)
  }

  function clearPoppedSapExpr () {
    poppedSapExpr.value = null;
    poppedSapNewName.value = "";
    poppedSapNewRouteTerm.value = "";
    poppedSapNewGalenic.value = "";
    sapConcept.value = null;
    cancelConceptRequest();
    sapConceptStatus.value = SapConceptStatus.Idle;
  }

  function setPoppedSapNewName(newText: string){
    poppedSapNewName.value = newText
  }
  function setPoppedSapNewRouteTerm(newText: string){
    poppedSapNewRouteTerm.value = newText
  }
  function setPoppedSapNewGalenic(newText: string){
    poppedSapNewGalenic.value = newText
  }

  async function evolveFullSapExpr(parsedDoses: { text: string, days: string, asNeeded: boolean }[]) {
    if (poppedSapExpr.value === null) { return; }
    if (!poppedSapExpr.value.expressionCcl?.expressionCclId) { return; }

    const appUserStore = useAppUserStore();
    const lang = appUserStore?.currentAppUserData?.preferences?.menuLanguageIso || 'fr';
    const original = poppedSapExpr.value;
    const newSapName = (poppedSapNewName.value === original.matchedText) ? null : poppedSapNewName.value
    const newRouteTerm = (poppedSapNewRouteTerm.value === original.sctAdministrationRouteTerm) ? null : poppedSapNewRouteTerm.value
    const newGalenic = (poppedSapNewGalenic.value === original.galenic) ? null : poppedSapNewGalenic.value
    const newMultidosage = serializeDoses(parsedDoses, lang);
    const doses = parsedDoses.map(toDoseDto);

    const isOldDurationExpr = (original.durationType === "Duration" && !isExprOfCurrEntry(original));
    const isStoppedExpr = (original.durationType === "Until");
    // No changes detected, skip update
    if (!isOldDurationExpr && !isStoppedExpr && newSapName === null && newRouteTerm === null && newGalenic === null && newMultidosage === original.multiDosageText) {
        return;
    }

    const expression_CclId = original.expressionCcl.expressionCclId;
    sapConcept.value = null;
    cancelConceptRequest();
    return await patientapi.evolveSapFull(
      expression_CclId.toString(),
      newSapName,
      newRouteTerm,
      newGalenic,
      newMultidosage,
      doses,
      currentEntryId.value,
      poppedSapExpr.value.patientTreatmentId
    );
  }

  async function createNewSapExpr(
      conclusionId: string,
      newSapName: string,
      newRouteTerm: string,
      newGalenic: string,
      parsedDoses: { text: string, days: string, asNeeded: boolean }[],
      sctSubstanceKey: string | null = null
    ) {
    const appUserStore = useAppUserStore()
    const lang = appUserStore?.currentAppUserData?.preferences?.menuLanguageIso || 'fr'
    const newMultidosage = serializeDoses(parsedDoses, lang)
    const doses = parsedDoses.map(toDoseDto)
    //Default Infos
    const medicationSuggestions = appUserStore.currentAppUserData.preferences.medicationSuggestions || [];
    const suggestion = findMedicationPreference(medicationSuggestions, newSapName, sctSubstanceKey);
    const defaultPatientInfo = getNewPatientInfo(suggestion);
    const defaultPharmacyInfo = getNewPharmacyInfo(suggestion);
    //Name
    const sapName = gen.capitalize(newSapName);
    const relationshipId = currentEntryId.value;
    const newSapExpr = conclusionId
      ? await patientapi.addSapToConclusion(conclusionId, sapName, newRouteTerm, newGalenic, newMultidosage, doses, relationshipId, defaultPatientInfo, defaultPharmacyInfo)
      : await patientapi.addSapToNullConclusion(sapName, newRouteTerm, newGalenic, newMultidosage, doses, relationshipId, defaultPatientInfo, defaultPharmacyInfo);

    if (currentDocId.value) {
      const fullPatientDoc = await patientapi.getPatientDocument(currentDocId.value, relationshipId);
      updateCurrentDocData(fullPatientDoc);
    }
    return newSapExpr;
  }

  async function deleteOrStopSap(sapExpr: any) {
    const expression_CclId = sapExpr?.expressionCcl?.expressionCclId;
    if (!expression_CclId) return;
    const currEntryRelationshipId = currentEntryId.value;
    const concept = findById(commonConcepts.value, "conceptGroupId", sapExpr.conceptGroupId);
    const conceptEntryRelationshipId = concept?.definingSapExpression.entry.relationshipId;
    if (currEntryRelationshipId === conceptEntryRelationshipId) {
      await patientapi.deleteSap(expression_CclId.toString(), currEntryRelationshipId, sapExpr.patientTreatmentId);
    } else {
      await patientapi.stopConcept(expression_CclId.toString(), sapExpr.expressionId.toString(), currEntryRelationshipId, sapExpr.patientTreatmentId);
    }
  }


  async function loadSapConcept() {
    const conceptGroupId = poppedSapExpr.value?.conceptGroupId;
    if (!conceptGroupId) {
      cancelConceptRequest();
      sapConcept.value = null;
      sapConceptStatus.value = SapConceptStatus.Idle;
      return;
    }
    if (sapConcept.value?.conceptGroupId === conceptGroupId) {
      return;
    }
    cancelConceptRequest();
    const controller = new AbortController();
    conceptRequest = controller;
    sapConcept.value = null;
    sapConceptStatus.value = SapConceptStatus.Loading;
    try {
      const result = await folderPatientApi.getConceptGroupByIdAndPatient(
        { patientId: currentPatientId.value, conceptGroupId: conceptGroupId, activeDoseTimingFilter: true },
        { signal: controller.signal },
      );
      if (conceptRequest !== controller) return;
      sapConcept.value = result;
      sapConceptStatus.value = SapConceptStatus.Idle;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      if (conceptRequest !== controller) return;
      sapConceptStatus.value = SapConceptStatus.Error;
    }
  }

  function cancelConceptRequest() {
    conceptRequest?.abort();
    conceptRequest = null;
  }

  function deleteRelativeFromCurrentPatient(relative: {personWithRoleId: string}) {
    currentPatient.value.relatives = currentPatient.value.relatives.filter((rel: {personWithRoleId: string}) => rel.personWithRoleId !== relative.personWithRoleId)
  }
  
  //currentCsId (item1), currentCclId (item2), currentResultId (item3)
  const currentCsId = ref<string | null>(null);
  const currentCclId = ref<string | null>(null);
  const currentResultId = ref<string | null>(null);
  const currentCs = computed(() => {
    if (!currentCsId.value || !allEntries.value) return null
    return csList.value.find(e => e.relationshipId === currentCsId.value) ?? null
  })
  const currentCcl = computed(() => {
    if (!currentCs.value || !currentCclId.value) return null
    return currentCs.value.conclusionGroups?.find(
      (ccl: any) => ccl.conclusionGroupId === currentCclId.value
    ) ?? null
  })
  const currentResult = computed(() => {
    if (!currentCcl.value || !currentResultId.value) return null
    return currentCcl.value.resultsArr?.find(
      (expr: any) => expr.expressionCclId === currentResultId.value
    ) ?? null
  })
  function setCurrentCs(itemObj: any) {
    currentCsId.value = itemObj?.relationshipId ?? null
  }
  function setCurrentCcl(itemObj: any) {
    currentCclId.value = itemObj?.conclusionGroupId ?? null
  }
  function setCurrentResult(itemObj: any) {
    currentResultId.value = itemObj?.expressionCclId ?? null
  }

  //For cclExpr (leftBar)
  function clearCurrExpr_Ccl() {
    currentCsId.value = null;
    currentCclId.value = null;
    currentResultId.value = null;
    clearBodyParts();
  }

  //For 'result suggestions'
  const activeResultSuggestion = ref("")
  function clearActiveResultSuggestion() {
    activeResultSuggestion.value = ""
  }  
  function setActiveResultSuggestion (suggestion: string) {
    activeResultSuggestion.value = suggestion
  }
  const isManualSet = ref(false)
  function setIsManualSet(isManuel: boolean) {
    isManualSet.value = isManuel
  } 

  //For 'result suggestions' - new text
  const newEntryText = ref("");
  function setNewEntryText (newText: string) {
    newEntryText.value = newText
  }
  
  const newEntryUnit = ref("");
  function setNewEntryUnit (newUnit: string) {
    newEntryUnit.value = newUnit
  }

  //For bodyParts
  const newBodyPart = ref("");
  function clearNewBodyPart () {
    newBodyPart.value = ""
  }
  const bodyParts = reactive([]);
  function clearBodyParts() {
    bodyParts.length = 0
  }
  function switchBodyPart (bodyPart: string) {
    newBodyPart.value = bodyPart
    gen.arrSwitch(bodyPart, bodyParts)
  }
  function syncBodyParts (recognizedBodyParts: Array<string>) {
    bodyParts.length = 0
    bodyParts.push(...recognizedBodyParts);
  }


  //For 'focus'
  const conclusionIdToFocus = ref("");
  function setConclusionIdToFocus (cclId: string) {
    conclusionIdToFocus.value = cclId
  }
  const conclusionIdToFocusOnExpr = ref("");
  function setConclusionIdToFocusOnExpr (cclId: string) {
    conclusionIdToFocusOnExpr.value = cclId
  }  

  //For 'docTemplate'
  const middleTab = ref("");    //e.g. resultSuggestions, cclSuggestions, docTemplate
  function setMiddleTab (keyWord: string) {
    middleTab.value = keyWord
  }

  const middleRefSheet = ref(null);
  async function setMiddleRefSheet (referenceSheet: any) {
    middleRefSheet.value = referenceSheet
  }

  const middleReferenceDocument = ref(null);
  async function setMiddleReferenceDocument (referenceDocument: any) {
    middleReferenceDocument.value = referenceDocument
  }

  function emptyCurrentDoc(){
    currentDocId.value = '';
    currentDoc.value = null;
    middleRefSheet.value = null;
    middleReferenceDocument.value = null;
    clearActiveField()
  }  
  function setCurrentDoc(patientDoc?: PatientDocumentUiDto) {
    if (!patientDoc) {
      emptyCurrentDoc();
      return
    }
    currentDocId.value = `${patientDoc.patientDocument.patientDocumentId}`;
    currentDoc.value = patientDoc;

    const patientDocument = patientDoc.patientDocument;
    const version = getPatientDocumentVersion(patientDocument);
    setSelectedDocumentOption({
      value: patientDocument.patientDocumentId,
      label: computeRelativeDate(
        patientDocument.entry.entryStart,
        currentEntryDate.value
      ) + " - " + patientDocument.originalText,
      status: patientDocument.templateStatus,
      documentGroupInternalCode: patientDocument.documentGroup.internalCode,
      version: version ? t('common.version', { version: version.version }) : undefined
    }, version ? {
      value: version.patientDocumentId,
      label: t('common.version', { version: version.version })
    } : undefined);
  }

  const setSelectedDocumentOption = (doc: PatientDocumentOption, docVersion?: PatientDocumentVersionOption) => {
    selectedDocVersionOption.value = docVersion ? docVersion : null;
    selectedDocOption.value = doc;
  }

  const setSelectedDocumentVersionOption = (docVersion: PatientDocumentVersionOption) => {
    selectedDocVersionOption.value = docVersion;
  }

  function updateCurrentDocData(patientDoc: any) {
    if (!patientDoc) {
      return;
    } 

    if ( currentDocId.value == patientDoc.patientDocument.patientDocumentId) {
      currentDoc.value = patientDoc;
    }
  }
  
  //Field copy-paste
  const activeFieldId = ref(null);
  const activeFieldElement = ref(null);
  function setActiveField(fieldId: any, fieldElement: any) {
    activeFieldId.value = fieldId
    activeFieldElement.value = fieldElement
  }
  function clearActiveField() {
    activeFieldId.value = null
    activeFieldElement.value = null
  }
  function insertTextAtCursor(textToInsert: string) {
    if (!activeFieldElement.value) {
      console.warn('No textarea selected');
      return false;
    }    
    const element = activeFieldElement.value;
    const start = element.selectionStart;
    const end = element.selectionEnd;
    const currentValue = element.value;
    // Insert
    const newValue = currentValue.substring(0, start) +
                      textToInsert +
                      currentValue.substring(end);    
    element.value = newValue;
    element.dispatchEvent(new Event('input', { bubbles: true }));
    element.focus();    
    // Set cursor position after focus
    setTimeout(() => {
      const newCursorPosition = start + textToInsert.length;
      element.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 0);    
    return true;
  }

  //For middle column suggestions
  const bioTab = ref("general");                     //e.g.general, immuno, bioch, ...
  function setBioTab (keyWord: string) {
    bioTab.value = keyWord;
  }

  const currCclSuggestionsFromResults = ref(null);
  function setCurrCclSuggestionsFromResults (listsObj: unknown) {
    currCclSuggestionsFromResults.value = listsObj
  }

    const currSuggestionsForDashboard= ref(null);
  function setCurrSuggestionsForDashboard (listsObj: unknown) {
    currSuggestionsForDashboard.value = listsObj
  }

  const currCclSuggestionsFromCclText = ref(null);
  const currCclResultKeys = ref(null); 

  async function updateCclSuggestionsForResults(currCclId: string) {
    if (!isEditable.value) return;
    if (!currCclId) {
      currCclSuggestionsFromResults.value = null;
      currCclResultKeys.value = null;
      return
    }
    const refSheetSuggestions = await appuserapi.getRefSheetsForCclGroup(currCclId)
    currCclSuggestionsFromResults.value = refSheetSuggestions
    const resultKeys = await patientapi.getRefResultKeys(currCclId)
    currCclResultKeys.value = resultKeys    
  }
  
  async function updateCclSuggestionsForCclText(currCclText: string) {
    if (!currCclText) {
      currCclSuggestionsFromCclText.value = null
      return
    }
    const evaluatedCcl = await patientapi.evaluateCclText(currCclText)
    const cclTextSuggestions = createSuggestionsFromCclTextList(evaluatedCcl)
    currCclSuggestionsFromCclText.value = cclTextSuggestions
  }

  const csListRefreshKey = ref(0);

  function requestCsListRefresh() {
    csListRefreshKey.value++;
  }

  //For 'history'
  const historyFilter = ref("");
  function setHistoryFilter (rootGroup: string) {
    historyFilter.value = rootGroup
  }

  //Drag n' drop
  const dndType = ref("");
  function setDndType (keyWord: string) {
    dndType.value = keyWord
  }  
  const dndId = ref("");
  function setDndId (id: string) {
    dndId.value = id
  }
  const dndObject = ref(null);
  function setDndObject (object: any) {
    dndObject.value = object
  }
  function clearDnd () {
    dndType.value = ""
    dndId.value = ""
    dndObject.value = null
  }

  // modalPatient
  const showPatientModal = ref(false);
  const patientModalTab = ref('notes');
  function setPatientModalTab (keyword: string) {
    patientModalTab.value = keyword;
  }
  function openPatientModal(initialTab = '') {
    if (initialTab !== '') {
      patientModalTab.value = initialTab;
    }
    showPatientModal.value = true;
  }
  function closePatientModal() {
    showPatientModal.value = false;
  }
  const modalPatient = ref(null)
  function setCurrentAsModalPatient() {
    modalPatient.value = currentPatient.value;
    modalPatientNotes.value = currentPatientNotes.value;
  }
  async function setModalPatient(patientId: string) {
    const oldModalPatientId = modalPatient.value?.personWithRoleId;
    if (oldModalPatientId && oldModalPatientId === patientId) return;
    modalPatient.value = null;
    const [patientRes, notes] = await Promise.all([
      patientapi.getPatientInfo(patientId),
      getPatientTasksForSticky(patientId),
    ]);
    modalPatient.value = patientRes.patient;
    modalPatientNotes.value = notes;
  }

  const modalPatientNotes = ref(null)
  const currentPatientNotes = ref(null)
  async function reloadModalPatientNotes(patientId: string) {
    const notes = await getPatientTasksForSticky(patientId)
    modalPatientNotes.value = notes
    if (currentPatient.value?.personWithRoleId === patientId) {
      currentPatientNotes.value = notes
    }
  }

  //Return
  return {
    csListRefreshKey,
    requestCsListRefresh,
    setCurrentEntryDate,
    //PatientModal, filled with modalPatient
    showPatientModal,
    patientModalTab,
    setPatientModalTab,
    openPatientModal,
    closePatientModal,
    modalPatient,
    setModalPatient,
    setCurrentAsModalPatient,
    modalPatientNotes,
    currentPatientNotes,
    reloadModalPatientNotes,

    // PoppedSap
    sapConceptStatus,
    patchSapConcept,
    poppedSapExpr,
    poppedSapNewName,
    poppedSapNewRouteTerm,
    poppedSapNewGalenic,
    createNewSapExpr,
    evolveFullSapExpr,
    setPoppedSapExpr,
    patchPoppedSapExpr,
    deleteOrStopSap,
    clearPoppedSapExpr,
    setPoppedSapNewName,
    setPoppedSapNewRouteTerm,
    setPoppedSapNewGalenic,
    activeResultSuggestion,
    clearActiveResultSuggestion,
    setActiveResultSuggestion,
    showAnnotations,
    dedupeById,
    // Loading state
    loadingState,
    isLoading,
    isFullyLoaded,

    // Raw
    _currentEntry,
    _allEntriesData,
    _commonConceptsData,
    _conceptsData,

    // Computed collections
    allExpressions,
    patientDocument,
    allPatientDocuments,
    allEntries,
    concepts,
    commonConcepts,

    // Simple refs
    sapConcept,
    currentPatient,
    csList,
    ecosytems,
    currentNullCclId,
    currentPatientCalendar,

    // Helper getters
    currentEntry,
    currentEntryId,
    currentPatientId,    
    isEditable,
    currentEntryDate,
    hasCurrentEntry,
    currentHealthPro,
    currentDelegateUuid,
    
    // Actions
    loadPatientFull,
    reloadCurrentEntry,
    reloadAfterHistoryChange,
    reloadAfterMedicationChange,
    reloadPatientOnly,
    createNewEntry,
    updateExpression,
    clearCurrentEntry,

    setCurrentPatientCalendar,
    isExprOfCurrEntry,
    conclusionIdToFocus,
    setConclusionIdToFocus,
    conclusionIdToFocusOnExpr,
    setConclusionIdToFocusOnExpr,
    middleTab,
    setMiddleTab,
    middleRefSheet,
    setMiddleRefSheet,
    middleReferenceDocument,
    setMiddleReferenceDocument,
    currentDocId,
    emptyCurrentDoc,
    setCurrentDoc,
    selectedDocOption,
    setSelectedDocumentOption,
    selectedDocVersionOption,
    setSelectedDocumentVersionOption,
    bioTab,
    setBioTab,
    currCclSuggestionsFromResults,
    setCurrCclSuggestionsFromResults,
    currSuggestionsForDashboard,
    setCurrSuggestionsForDashboard,
    currCclSuggestionsFromCclText,
    currCclResultKeys, 
    updateCclSuggestionsForResults,
    updateCclSuggestionsForCclText,
    clearCurrExpr_Ccl,
    newEntryText,
    setNewEntryText,
    isManualSet,
    setIsManualSet,
    newEntryUnit,
    setNewEntryUnit,
    currentCs,
    setCurrentCs,
    currentCcl,
    setCurrentCcl,
    currentResult,
    setCurrentResult,
    newBodyPart,
    bodyParts,
    clearNewBodyPart,
    clearBodyParts,
    switchBodyPart,
    syncBodyParts,
    //HistoryFilter
    historyFilter,
    setHistoryFilter,
    //DND
    dndType,
    setDndType,
    dndId,
    setDndId,
    dndObject,
    setDndObject,    
    clearDnd,
    currentDoc,
    updateCurrentDocData,
    activeFieldId,
    activeFieldElement,
    setActiveField,
    clearActiveField,
    insertTextAtCursor,
    currentCsId,
    currentCclId,
    currentResultId,
    deleteRelativeFromCurrentPatient,
    updateExpressionAfterCorrection,
    setReloadEcosystemData,
    reloadEcosystemData,
    setReloadMonitoredData,
    reloadMonitoredData,
    setReloadHistoryData,
    reloadHistoryData,
    setReloadTreatmentData,
    reloadTreatmentData,
    setPatientHistoryResponse,
    patientHistoryResponse,
    setPatientSummaryResponse,
    patientEcosystemResponse,
    setPatientEcosystemResponse,
    patientMonitoredResponse,
    setPatientMonitoredResponse,
    reloadSummaryData,
    setReloadSummaryData,
    moveAppointmentFromPatient,
    setMoveAppointmentFromPatient,
    patientLightResponse,
    setMoveAppointmentFromCalendar,
    moveAppointmentFromCalendar,
    setIsMoveAppointmentAction,
    isMoveAppointmentAction,
    setCurrentEvent,
    currentEvent,
    setAppointmentId,
    appointmentId
  }
})
